import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Shield, Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";

const navLinks = [
  { to: "/", label: "Overview", end: true },
  { to: "/services", label: "Services" },
  { to: "/coverage", label: "Coverage" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function MarketingLayout() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      <header
        data-testid="marketing-header"
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <div className="h-8 w-8 bg-[#0B192C] flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-[15px] font-semibold tracking-tight">
                MEAD PROPERTY
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                Preservation · LLC
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${
                    isActive
                      ? "text-[#0B192C]"
                      : "text-slate-500 hover:text-[#0B192C]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/quote"
              data-testid="header-quote-btn"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-medium rounded-sm"
            >
              Request quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="px-4 py-2 border border-slate-300 text-slate-900 text-sm font-medium rounded-sm hover:bg-slate-50"
            >
              Client login
            </Link>
          </div>
        </div>
      </header>

      <main key={pathname} className="flex-1">
        <Outlet />
      </main>

      <footer
        data-testid="marketing-footer"
        className="bg-[#0B192C] text-slate-300 mt-24 grain-overlay"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-white/10 flex items-center justify-center">
                <Shield className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display text-white text-lg tracking-tight">
                  Mead Property Preservation LLC
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
                  Institutional-grade field services
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-md">
              A dedicated field-service partner for financial institutions,
              mortgage servicers, and asset management firms. Documented
              inspections, defensible reporting, and audit-ready workflows.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-4">
              Services
            </div>
            <ul className="space-y-2 text-sm">
              <li>Property inspections</li>
              <li>Lawn maintenance</li>
              <li>Winterization</li>
              <li>Debris removal</li>
              <li>Lock changes</li>
              <li>REO cleanouts</li>
              <li>Boarding</li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-slate-500" /> (973) 715-6578
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-slate-500" /> services@meadpp.com
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-slate-500" /> Serving New Jersey &amp; Pennsylvania
              </li>
            </ul>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.28em] px-2 py-1 border border-slate-700 text-slate-400">
                Licensed
              </span>
              <span className="text-[10px] uppercase tracking-[0.28em] px-2 py-1 border border-slate-700 text-slate-400">
                Insured
              </span>
              <span className="text-[10px] uppercase tracking-[0.28em] px-2 py-1 border border-slate-700 text-slate-400">
                Bonded
              </span>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-4 text-xs text-slate-500">
            <div>© {new Date().getFullYear()} Mead Property Preservation LLC. All rights reserved.</div>
            <div className="font-mono-alt tracking-wider">FIELD-OPS / v1.0</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
